<template>
  <div class="interview-slot-selection mt-5">
    <div>
      <div v-if="isLoadingSlotsMeta">
        <v-skeleton-loader type="date-picker-days" v-for="i in 2" :key="i" />
      </div>
      <BasicCalendar
        v-show="!isLoadingSlotsMeta"
        :events="events"
        :weekdays="[1, 2, 3, 4, 5, 6, 0]"
        @onDayClick="onDayClick"
        @onDateChange="onDateChange"
      />
    </div>
    <div class="slots-time-range-wrapper">
      <span class="mr-2"> Show available slots from</span>
      <TimeRangePicker
        :fromHour="startTime"
        :toHour="endTime"
        @updateRange="updateTimeRange"
        disableValidation
      />
    </div>
    <div class="my-5">
      <h2 class="mb-5">Select Slot</h2>
      <div v-if="isLoadingAvailableSlots">
        <v-skeleton-loader type="list-item" v-for="i in 2" :key="i" />
      </div>
      <div v-else>
        <div v-if="isEmpty(availableSlots)" class="mt-2">
          No slots found
        </div>
        <div v-if="availableSlots">
          <div
            v-for="(hourRangeSlots, hourRangeKey) in availableSlots"
            :key="hourRangeKey"
          >
            {{ hourRangeKey }}
            <RadioButton
              :options="hourRangeSlots"
              :selectedValue="interviewSlotData"
              @select="val => updateSlotData(val)"
              showGroupInRow
            />
          </div>
        </div>
        <div
          v-if="showValidationErrors && !interviewSlotData"
          class="text-error mt-5"
        >
          This field is required
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS } from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import { map, delay, isEqual, sortBy, isEmpty } from "lodash";
import RadioButton from "@/components/common/RadioButton";
import BasicCalendar from "@/components/common/BasicCalendar";
import {
  getColorBySlotAvailability,
  getAvailableSlotParamsForGivenMonth,
  changeCalendarDaySelected,
  removeEventsFromInactiveDayFields,
  getSlotsGroupedByTimeRange
} from "@/views/bookings/BookingInterviews/modules/utils";
import TimeRangePicker from "@/components/common/TimePicker/TimeRangePicker";
import moment from "moment";
import { DATE_TIME_FORMAT } from "@/utils/time";
import {
  INITIAL_START_TIME_VALUE,
  INITIAL_END_TIME_VALUE
} from "@/constants/interviews";

const { mapActions: mapInterviewActions } = createNamespacedHelpers(
  INTERVIEWS_NAMESPACE
);

export default {
  components: {
    RadioButton,
    BasicCalendar,
    TimeRangePicker
  },
  props: {
    interviewFormatId: {
      type: String,
      required: true
    },
    interviewers: {
      type: Array,
      required: true
    },
    showValidationErrors: Boolean
  },
  created() {
    this.loadSlotsMetaData();
  },
  data() {
    return {
      interviewSlotData: null,
      startTime: INITIAL_START_TIME_VALUE,
      endTime: INITIAL_END_TIME_VALUE,
      events: [],
      isLoadingAvailableSlots: false,
      isLoadingSlotsMeta: false,
      availableSlots: null,
      selectedDay: null,
      calendarDate: new Date().toISOString()
    };
  },
  computed: {
    availableSlotsParamsForGivenMonth() {
      return getAvailableSlotParamsForGivenMonth({
        date: this.calendarDate,
        users: this.interviewers
      });
    }
  },
  methods: {
    ...mapInterviewActions({
      fetchInterviewSlots: FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS
    }),
    updateTimeRange({ fromHour, toHour }) {
      this.startTime = fromHour;
      this.endTime = toHour;
      if (this.selectedDay) {
        this.loadAvailableSlots();
      }
    },
    handleChange(value) {
      this.$emit("onSelectSlot", value);
    },
    async onDateChange(newDate) {
      this.calendarDate = newDate;
      await this.loadSlotsMetaData();
      this.selectedDay = null;
      this.availableSlots = null;
    },
    onDayClick({ date }) {
      this.updateSlotData(null);
      if (this.selectedDay) {
        changeCalendarDaySelected({ date: this.selectedDay, selected: false });
        this.selectedDay = null;
      }
      this.selectedDay = date;
      changeCalendarDaySelected({ date, selected: true });
      this.loadAvailableSlots();
    },
    updateSlotData(value) {
      this.interviewSlotData = value;
      this.$emit("onSelectSlot", value);
    },
    async loadAvailableSlots() {
      this.isLoadingAvailableSlots = true;
      const formattedDate = moment(this.selectedDay).format(DATE_TIME_FORMAT);
      const { data } = await this.fetchInterviewSlots({
        interviewFormatId: this.interviewFormatId,
        params: {
          users: this.interviewers,
          all_users_available: 1,
          overlapping_slots: 1,
          start_time: this.startTime,
          end_time: this.endTime,
          start_date: formattedDate,
          end_date: formattedDate
        }
      });
      if (data) {
        this.adjustAvailableSlots(data);
      }
      this.isLoadingAvailableSlots = false;
    },
    async loadSlotsMetaData() {
      this.isLoadingSlotsMeta = true;
      const data = await this.fetchInterviewSlots({
        interviewFormatId: this.interviewFormatId,
        params: {
          ...this.availableSlotsParamsForGivenMonth,
          only_meta: 1
        }
      });
      if (data) {
        this.adjustCalendar(data.meta);
      }
      this.isLoadingSlotsMeta = false;
    },
    adjustCalendar({ maxPerDay, perDay }) {
      const dates = Object.keys(perDay);
      this.events = map(dates, date => ({
        name: date,
        start: date,
        end: date,
        color: getColorBySlotAvailability(perDay[date], maxPerDay)
      }));
      delay(() => {
        removeEventsFromInactiveDayFields();
        changeCalendarDaySelected({ date: this.selectedDay, selected: true });
      }, 1000);
    },
    adjustAvailableSlots(availableSlots) {
      this.availableSlots = getSlotsGroupedByTimeRange(availableSlots);
    },
    isEmpty
  },
  watch: {
    interviewers(newValue, prevValue) {
      if (!isEqual(sortBy(newValue), sortBy(prevValue))) {
        this.selectedDay = null;
        this.loadSlotsMetaData();
      }
    }
  }
};
</script>

<style lang="scss">
.interview-slot-selection {
  .slots-time-range-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 20px 0;

    .range-picker-wrapper {
      display: flex;
    }
  }
  .radio {
    @include interview-radio;

    .v-radio {
      margin-right: 0px;
      label {
        top: 2px;
        left: 5px !important;
      }
    }

    @media only screen and (min-width: $tablet-breakpoint + 1px) {
      .radio--standard label {
        padding-left: 15px;
      }
    }
  }
}
</style>
